import { useState, useEffect } from 'react'
import { VITE_API_BASE } from '../config/apiConfig'

const API = VITE_API_BASE

// 剧目→七牛云目录映射（同FairytaleTheatre.jsx）
const PROD_DIR_MAP = {
  '孤独的博小兔': '3.孤独的博小兔',
  '笨鸟先飞': '7.笨鸟先飞',
  '今天和明天': '8.今天和明天',
  '最好的父母': '4.最好的父母',
  '珍惜': '5.珍惜',
  '信守承诺': '6.信守承诺',
  '博小兔和小猪胖胖': '1.博小兔和小猪胖胖',
  '博小兔数星星': '2.博小兔数星星',
}

const PROD_IMG_COUNTS = {
  '3.孤独的博小兔': 13,
  '7.笨鸟先飞': 13,
  '8.今天和明天': 12,
  '4.最好的父母': 14,
  '5.珍惜': 12,
  '6.信守承诺': 10,
  '1.博小兔和小猪胖胖': 5,
  '2.博小兔数星星': 5,
}

const getProdImages = (prodName, size = 'thumb') => {
  const dir = PROD_DIR_MAP[prodName]
  if (!dir) return []
  const baseUrl = `https://cdn.boxiaotu.cn/fairytale/${encodeURIComponent(dir)}`
  const tail = size === 'full' ? '?imageMogr2/thumbnail/1500x' : '?imageMogr2/thumbnail/800x'
  const count = PROD_IMG_COUNTS[dir] || 5
  const useTeacherPrefix = dir === '1.博小兔和小猪胖胖' || dir === '2.博小兔数星星'
  if (useTeacherPrefix) {
    return Array.from({length: count}, (_, i) => `${baseUrl}/${encodeURIComponent('老师绘本图'+(i+1)+'.jpg')}${tail}`)
  }
  if (dir === '6.信守承诺') {
    return [461,459,457,455,453,451,449,448,447,446].map(n => `${baseUrl}/${encodeURIComponent('微信图片_2023051610' + n + '.jpg')}${tail}`)
  }
  return Array.from({length: count}, (_, i) => `${baseUrl}/${i+1}.jpg${tail}`)
}

const SECTION_TYPE_LABELS = {
  story: '📖 故事',
  craft: '🎨 手工',
  drama: '🎭 戏剧游戏',
  break: '☕ 休息',
  discussion: '💬 讨论',
  performance: '🎬 展演',
}

export default function Courseware() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedProdIdx, setSelectedProdIdx] = useState(0)
  const [lightbox, setLightbox] = useState(null)

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API}/courseware/data`)
      const d = await res.json()
      if (d.success && d.data?.productions?.length > 0) {
        setData(d.data)
      }
    } catch (err) {
      console.error('加载课件失败', err)
    }
    setLoading(false)
  }

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
        <div style={{ fontSize: 14 }}>加载中...</div>
      </div>
    )
  }

  if (!data || !data.productions?.length) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
        <p>暂无课件数据</p>
      </div>
    )
  }

  const selectedProd = data.productions[selectedProdIdx] || data.productions[0]
  const lessons = selectedProd.lessons || []

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <h2 style={{ fontSize: 20, margin: '0 0 4px' }}>📚 课件中心</h2>
      <p style={{ fontSize: 13, color: '#888', margin: '0 0 20px' }}>
        共 {data.productions.length} 部课程 · {data.productions.reduce((s, p) => s + (p.lessons?.length || 0), 0)} 课时
        {data.version && <span style={{ marginLeft: 12 }}>版本 {data.version}</span>}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 16, alignItems: 'start' }}>
        {/* 左侧：课程选择列表 */}
        <div style={{ background: '#fff', borderRadius: 8, padding: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 8, padding: '0 4px' }}>课程列表</div>
          {data.productions.map((prod, idx) => (
            <div
              key={prod.id}
              onClick={() => setSelectedProdIdx(idx)}
              style={{
                padding: '10px 12px',
                marginBottom: 6,
                borderRadius: 6,
                cursor: 'pointer',
                background: selectedProdIdx === idx ? '#e6f7ff' : '#fff',
                border: selectedProdIdx === idx ? '1px solid #1890ff' : '1px solid #f0f0f0',
                color: selectedProdIdx === idx ? '#1890ff' : '#333',
                fontSize: 13,
                fontWeight: selectedProdIdx === idx ? 600 : 400,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 32, height: 32, borderRadius: 6, overflow: 'hidden', flexShrink: 0, background: '#f0f0f0' }}>
                  <img
                    src={getProdImages(prod.name, 'thumb')[0]}
                    alt={prod.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => { e.target.style.display = 'none' }}
                  />
                </div>
                <div>
                  <div style={{ marginBottom: 2, fontSize: 13 }}>{prod.name}</div>
                  <div style={{ fontSize: 11, color: '#999' }}>{prod.lessons?.length || 0} 课时</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 右侧：选中课程的课时卡片 */}
        <div>
          {/* 课程标题 */}
          <div style={{
            background: '#fff',
            borderRadius: 8,
            padding: '16px 20px',
            marginBottom: 16,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}>
            <h3 style={{ fontSize: 18, margin: 0, color: '#333' }}>{selectedProd.name}</h3>
            {selectedProd.description && (
              <p style={{ fontSize: 13, color: '#555', margin: '8px 0 4px', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{selectedProd.description}</p>
            )}
            <p style={{ fontSize: 12, color: '#999', margin: '6px 0 12px' }}>
              课程编号：{selectedProd.id} · {lessons.length} 个课时
            </p>
            {/* 插画画廊 */}
            {(() => {
              const images = getProdImages(selectedProd.name, 'full')
              if (images.length <= 1) return null
              return (
                <div style={{
                  display: 'flex', gap: 6, overflowX: 'auto',
                  paddingBottom: 6,
                  WebkitOverflowScrolling: 'touch',
                }}>
                  {images.map((url, idx) => (
                    <div key={idx} style={{
                      width: 80, height: 56, borderRadius: 6, overflow: 'hidden',
                      flexShrink: 0, background: '#f5f5f5',
                      border: '1px solid #f0f0f0', cursor: 'pointer'
                    }} onClick={() => setLightbox(url)}>
                      <img
                        src={url}
                        alt={`第${idx+1}幕`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        loading="lazy"
                        onError={(e) => { e.target.style.display = 'none' }}
                      />
                    </div>
                  ))}
                </div>
              )
            })()}
          </div>

          {/* 课时列表 */}
          {lessons.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>该课程暂无课时数据</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {lessons.map(lesson => (
                <LessonCard key={lesson.id} lesson={lesson} />
              ))}
            </div>
          )}
        </div>
      </div>
      {lightbox && (
        <div
          style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.85)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}}
          onClick={() => setLightbox(null)}
        >
          <img src={lightbox} style={{maxWidth:'90vw',maxHeight:'90vh',borderRadius:8,boxShadow:'0 4px 20px rgba(0,0,0,0.5)'}} />
        </div>
      )}
    </div>
  )
}

function LessonCard({ lesson }) {
  const [expanded, setExpanded] = useState(false)
  const sections = lesson.sections || {}
  const sectionKeys = Object.keys(sections)

  return (
    <div style={{
      background: '#fff',
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      overflow: 'hidden',
    }}>
      {/* 课时头部 */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '14px 18px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          userSelect: 'none',
        }}
      >
        <span style={{
          background: '#1890ff',
          color: '#fff',
          fontSize: 12,
          fontWeight: 600,
          padding: '3px 12px',
          borderRadius: 12,
          flexShrink: 0,
        }}>
          第{lesson.id}课
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 2 }}>
            {lesson.theme || '未设置主题'}
          </div>
          <div style={{ fontSize: 12, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {lesson.goal || ''}
          </div>
        </div>
        <div style={{ fontSize: 12, color: '#999', flexShrink: 0 }}>
          {sectionKeys.length} 个环节 · {sectionKeys.reduce((sum, k) => sum + (sections[k]?.length || 0), 0)} 项
        </div>
        <span style={{
          fontSize: 14, color: '#ccc',
          transform: expanded ? 'rotate(90deg)' : 'none',
          transition: 'transform 0.2s',
          flexShrink: 0,
        }}>
          ▶
        </span>
      </div>

      {/* 展开的环节详情 */}
      {expanded && (
        <div style={{ borderTop: '1px solid #f0f0f0', padding: '14px 18px', background: '#fafafa' }}>
          {sectionKeys.map((periodKey) => {
            const items = sections[periodKey] || []
            const periodLabel = periodKey === 'p0' ? '第一课时' : periodKey === 'p1' ? '第二课时' : periodKey
            return (
              <div key={periodKey} style={{ marginBottom: 16 }}>
                <div style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#666',
                  marginBottom: 8,
                  padding: '4px 10px',
                  background: '#e6f7ff',
                  borderRadius: 4,
                  display: 'inline-block',
                }}>
                  {periodLabel}
                </div>
                {items.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#ccc', paddingLeft: 10 }}>暂无环节</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {items.map((item, idx) => (
                      <div key={idx} style={{
                        padding: '10px 14px',
                        background: '#fff',
                        borderRadius: 6,
                        border: '1px solid #f0f0f0',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{
                            fontSize: 11,
                            padding: '1px 8px',
                            borderRadius: 8,
                            background: '#fff7e6',
                            color: '#d46b08',
                          }}>
                            {SECTION_TYPE_LABELS[item.type] || item.type || '环节'}
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 500, color: '#333' }}>{item.title}</span>
                          <span style={{ fontSize: 11, color: '#999', marginLeft: 'auto' }}>{item.duration} 分钟</span>
                        </div>
                        {item.content && (
                          <div style={{ fontSize: 12, color: '#666', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                            {item.content}
                          </div>
                        )}
                        {item.video && Object.values(item.video).some(v => v) && (
                          <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {item.video.animation && (
                              <VideoLink label="动画版" url={item.video.animation} color="#1890ff" />
                            )}
                            {item.video.paperPuppet && (
                              <VideoLink label="纸偶版" url={item.video.paperPuppet} color="#52c41a" />
                            )}
                            {item.video.handPuppet && (
                              <VideoLink label="手偶版" url={item.video.handPuppet} color="#722ed1" />
                            )}
                            {item.video.liveStage && (
                              <VideoLink label="真人舞台" url={item.video.liveStage} color="#eb2f96" />
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function VideoLink({ label, url, color }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = (e) => {
    e.stopPropagation()
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        style={{
          fontSize: 11,
          color: color || '#1890ff',
          padding: '2px 8px',
          borderRadius: 4,
          background: (color || '#1890ff') + '12',
          textDecoration: 'none',
          cursor: 'pointer',
        }}
      >
        🎬 {label}
      </a>
      <span
        onClick={handleCopy}
        style={{
          fontSize: 10,
          color: '#999',
          cursor: 'pointer',
          padding: '1px 4px',
          borderRadius: 3,
          background: '#f5f5f5',
        }}
        title="复制链接"
      >
        {copied ? '✓' : '📋'}
      </span>
    </div>
  )
}
